/**
 * Financial Period Form Component
 * Form for creating and editing financial periods
 */

import React, { useState, useEffect } from 'react';
import { financialPeriodService } from '../../services/financialPeriodService';
import './FinancialPeriodForm.css';

const FinancialPeriodForm = ({ period, onSubmit, onClose, isSubmitting }) => {
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    is_active: false
  });
  const [errors, setErrors] = useState({});

  // Initialize form data when editing
  useEffect(() => {
    if (period) {
      setFormData({
        name: period.name || '',
        start_date: period.start_date ? period.start_date.split('T')[0] : '',
        end_date: period.end_date ? period.end_date.split('T')[0] : '',
        is_active: period.is_active || false
      });
    }
  }, [period]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form data
    const validation = financialPeriodService.validatePeriodData(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    // Format data for API
    const formattedData = financialPeriodService.formatPeriodDataForAPI(formData);

    // Call onSubmit with appropriate parameters
    if (period) {
      onSubmit(period.id, formattedData);
    } else {
      onSubmit(formattedData);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>{period ? 'Edit Financial Period' : 'Create Financial Period'}</h2>
          <button className="close-btn" onClick={onClose} disabled={isSubmitting}>
            ×
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="form-section">
              <h3>Basic Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Period Name *</label>
                  <input
                    type="text"
                    className={errors.name ? 'error' : ''}
                    placeholder="Enter period name (e.g., 2024 Financial Year)"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.name && <span className="field-error">{errors.name}</span>}
                </div>
              </div>
            </div>

            {/* Date Range */}
            <div className="form-section">
              <h3>Date Range</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    className={errors.start_date ? 'error' : ''}
                    value={formData.start_date}
                    onChange={(e) => handleInputChange('start_date', e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.start_date && <span className="field-error">{errors.start_date}</span>}
                </div>

                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    className={errors.end_date ? 'error' : ''}
                    value={formData.end_date}
                    onChange={(e) => handleInputChange('end_date', e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.end_date && <span className="field-error">{errors.end_date}</span>}
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="form-section">
              <h3>Status</h3>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span className="checkmark"></span>
                  Active Period
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : (period ? 'Update Period' : 'Create Period')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FinancialPeriodForm;
